import englishMessages from 'ra-language-english';

export const en = {
  ...englishMessages,
  "Missing Refresh Token (audience: '', scope: 'openid profile email offline_access')":
    'Please log in to proceed!',
  app: {
    title: 'Anthropometry Atelier',
    subtitle: 'Biometrics & Somatotype Laboratory',
    modules: 'Modules',
    configure_modules: 'Module Configuration',
    configure_modules_tooltip: 'Configure Modules and Features',
    language: 'Language',
    espanol: 'Spanish',
    english: 'English',
  },
  auth: {
    welcome_title: 'Anthropometric Laboratory Access',
    welcome_subtitle: 'Official Biometrics & Somatotyping System',
    login_prompt: 'Please enter your authorized professional credentials to access patient dossiers.',
    email_or_username: 'Email Address or Username',
    password: 'Password',
    login_button: 'Enter Atelier',
    authenticating: 'Authenticating...',
    invalid_credentials: 'The credentials entered are invalid or not registered.',
    role_super_admin: 'Super Administrator',
    role_nutritionist: 'Nutritionist Specialist',
    logged_in_as: 'Session',
    logout: 'Log Out',
    restricted_title: 'Restricted Access',
    restricted_message: 'This confidential dossier belongs to another specialist. You do not have permissions to view or edit this record.',
    return_to_list: 'Return to My Patients',
    create_specialist: 'Register New Specialist',
    specialist_assigned: 'Assigned to your professional practice',
    filter_by_nutritionist: 'Filter by Specialist',
    all_specialists: 'All Specialists',
  },
  features: {
    title: 'Module & Feature Configuration',
    description:
      'Enable or disable modules to customize the laboratory interface based on your workflow. Changes apply instantly to menus and views.',
    presets_title: 'QUICK PRESETS',
    preset_all: 'All Modules (Complete)',
    preset_clinical: 'Basic Clinical (Patients & Evaluations)',
    preset_somatotype_only: 'Somatochart & References Only',
    preset_minimal: 'Minimal (Patients Only)',
    module_status: 'MODULE STATUS',
    active: 'ACTIVE',
    inactive: 'INACTIVE',
    reset: 'Reset Defaults',
    apply: 'Apply',
    user_title: 'Patient Dossier',
    user_desc:
      'Comprehensive management of clinical records, demographics, and background of athletes and patients.',
    measurement_title: 'Anthropometric Evaluations',
    measurement_desc:
      'Standardized ISAK measurement recording: skinfolds, body girths, and bone breadths.',
    nutritionist_title: 'Nutritionist Directory',
    nutritionist_desc:
      'Directory of laboratory specialists and practitioners in charge of assessments.',
    referenced_somatotype_title: 'Reference Somatotypes',
    referenced_somatotype_desc:
      'Catalog of elite reference morphological profiles by sport and gender.',
    results_analytics_title: 'Analytics & Somatochart',
    results_analytics_desc:
      'Heath-Carter somatotype calculation, Cartesian scatter plot, BMI, and fat percentages.',
    pdf_export_title: 'PDF Report Export',
    pdf_export_desc:
      'Generation and download of printable anthropometric evaluation reports.',
  },
  measurement_show: {
    title: 'Anthropometric Evaluation Sheet',
    protocol: 'ISAK PROTOCOL — CONTROL #%{control}',
    download_pdf: 'Download PDF Sheet',
    generating_pdf: 'Generating...',
    view_analytics: 'View Somatochart & Graphs',
    athlete: 'Athlete',
    registered_athlete: 'Registered athlete',
    nutritionist: 'Nutritionist',
    section_a: 'Section A: Basic Data & Evaluation',
    control_number: 'Control Number',
    height: 'Height',
    weight: 'Weight',
    wingspan: 'Wingspan',
    training_period: 'Training Period',
    notes: 'Evaluation Notes',
    section_b: 'Section B: Skinfolds (mm)',
    triceps: 'Triceps',
    subscapular: 'Subscapular',
    biceps: 'Biceps',
    iliac_crest: 'Iliac Crest',
    supraspinale: 'Supraspinale',
    abdominal: 'Abdominal',
    front_thigh: 'Front Thigh',
    medial_calf: 'Medial Calf',
    section_c: 'Section C: Body Girths (cm)',
    arm_relaxed: 'Relaxed Arm',
    arm_flexed: 'Flexed Arm',
    forearm: 'Forearm',
    wrist: 'Wrist',
    chest: 'Chest',
    waist: 'Waist',
    hip: 'Hip',
    thigh: 'Upper Thigh',
    thigh_max: 'Thigh Max',
    thigh_mid: 'Thigh Mid',
    calf: 'Calf',
    ankle: 'Ankle',
    section_d: 'Section D: Bone Breadths & Somatotype (cm)',
    biacromial: 'Biacromial',
    transverse_chest: 'Transverse Chest',
    anterior_posterior_chest: 'Anteroposterior Chest',
    biiliocristal: 'Bi-iliocristal',
    humerus: 'Humeral (Humerus)',
    femur: 'Femoral (Femur)',
    ref_somatotype: 'Reference Sport',
    xy_coords: 'Reference Coordinates (X, Y)',
    calculated_bmi: 'Calculated BMI',
    estimated_fat_mass: 'Estimated Fat Mass',
    loading: 'Loading measurement sheet...',
    error: 'Error loading evaluation.',
  },
  user_show: {
    dossier_title: 'ATHLETE / PATIENT DOSSIER — ID #%{id}',
    personal_data: 'Personal Information',
    contact_assignment: 'Contact & Assignment',
    evaluation_history: 'Anthropometric Evaluation History',
    body_comp_progression: 'Body Composition Progression',
    somatotype_chart: 'Somatotype Chart',
  },
  nutritionist_show: {
    specialist: 'SPECIALIST IN NUTRITION & ANTHROPOMETRY',
    dossier_title: 'Professional Registration File',
    created_at: 'Registration Date',
    updated_at: 'Last Update',
  },
  results: {
    title: 'Biometric Report & Morphological Diagnosis',
    control_caption: 'CONTROL #%{control} — ANTHROPOMETRIC ASSESSMENT',
    back_to_measurements: 'Back to Measurements',
    patient: 'Patient',
    height: 'Height',
    weight: 'Weight',
    bmi: 'BMI',
    loading: 'Loading biometric and morphological report...',
    tabulated_ledger: 'Official Tabulated Data Ledger',
    tabulated_subtitle: 'Body Composition Calculations, Physiological Indices & Heath-Carter Somatotype',
    table_indices: 'Table I: Physiological & Anthropometric Indices',
    table_fat_models: 'Table II: Fat Models & Body Masses',
    table_somatotype: 'Table III: Heath-Carter Somatotype Parameters',
    col_parameter: 'Variable / Parameter',
    col_value: 'Calculated Value',
    col_unit: 'Unit',
    unit_adimensional: 'Adimensional',
  },
  somatotype: {
    reference_profile: 'REFERENCE MORPHOLOGICAL PROFILE — ID #%{id}',
    main_activity: 'MAIN ACTIVITY',
    chart_title: 'SOMATOCHART GRAPH',
    current_profile: 'Current Profile',
    projected_ideal: 'Projected Ideal',
    target_min: 'MIN: 1.0',
    target_max: 'MAX: 10.0',
    target_goal: 'GOAL: 5.5',
    to_goal: '+0.42 TO GOAL',
    lean_mass_opt: 'LEAN MASS OPTIMIZATION',
    morphological_analysis: 'Morphological Analysis',
    dominant_profile: 'Dominant Profile',
    sport_aptitude: 'Aptitude',
    recommendation: 'Recommendation',
    center_title: 'High Performance Center',
    center_subtitle: 'Headquarters - Biomechanics & Nutrition Department',
    certification: 'VITALITY CERTIFICATION',
    edit: 'EDIT',
    new_entry: 'NEW ENTRY',
    edit_record: 'RECORD EDITING',
    data_record: 'Data Record',
    physiological_data: 'Physiological Data',
    enter_physiological: 'Enter physiological parameters for somatotype analysis.',
    components: 'SOMATOTYPE COMPONENTS',
    cartesian_coords: 'CARTESIAN COORDINATES',
    live_preview: 'Somatotype Chart Live Preview',
    profile_analysis: 'Profile Analysis',
    muscle_balance: 'Muscle Balance',
    adiposity_level: 'Adiposity Level',
    high: 'High',
    moderate: 'Moderate',
    low: 'Low',
    save: 'SAVE',
    delete_measurement: 'DELETE MEASUREMENT',
    sport: 'Sport',
    gender: 'Gender',
    male: 'MALE',
    female: 'FEMALE',
    act: 'Act.',
    subaquatic: 'Underwater Sports',
    dominant_prefix: 'Predominance',
    dominant_suffix: 'Muscular robustness is the key factor, with a moderate adipose tissue presence that facilitates neutral buoyancy and overall physical endurance.',
    aptitude_desc: 'High energy reserve capacity. Ideal for prolonged low-intensity immersion. Excellent hydrodynamic stability due to low center of mass.',
    rec_item_1: 'Focus on specific hypertrophic training.',
    rec_item_2: 'Strict pre-session glycemic management.',
    rec_item_3: 'Optimize power-to-weight ratio (W/kg).',
    reference_morphogram: 'Reference Somatotype Morphogram',
    cartesian_graphs: 'Cartesian Coordinates & Graphs',
    key_values: 'Key Somatotype Values',
    preview: 'Somatochart Preview',
    classification: 'Predominant Classification',
    title: 'Somatotype Calculation',
  },
  myroot: {
    download: 'Download',
    results: 'View Results',
    password: 'Password',
    addressAndPhone: 'Address & Phone',
    identity: 'Identity',
    male: 'Male',
    female: 'Female',
    mediciones: 'Measurements',
    AddressPhone: 'Address and Phone',
    pliegues: 'Skinfolds (mm)',
    perímetros: 'Perimeters (cm)',
    diametro: 'Diameter (cm)',
    somatotipo: 'Reference Somatotype',
    empty: 'No items created',
    grams: 'Grams',
    total_calories: 'Total Calories',
    simulate_results: 'Simulate Results',
    welcomelogin: 'Welcome to the Nutritionist Portal',
    login: 'Log In',
    bodyComposition: 'Body Composition',
    fatPercentageIndices: 'Fat Percentage Indices',
    welcomeScheduler: 'Evaluation Appointments This Week',
    ExpectedValues: 'Expected Values',
    'somatotipo de referencia': 'Reference Somatotype',
    'somatotipo actual': 'Current Somatotype',
    endomorph: 'Endomorph',
  },
  resources: {
    user: {
      name: 'User |||| Users',
      fields: {
        firstname: 'First Name',
        lastname: 'Last Name',
        email: 'Email',
        phone: 'Phone',
        address: 'Address',
        birthday: 'Birthday',
        gender: 'Gender',
        created_at: 'Created',
        updated_at: 'Updated',
        password: 'Password',
        confirm_password: 'Confirm Password',
        plans: 'Meal Plans',
        measurements: 'Measurements',
        nutritionist: 'Nutritionist',
      },
    },
    measurement: {
      name: 'Measurement |||| Measurements',
      fields: {
        nutritionist_id: 'Nutritionist',
        evaluation_date: 'Evaluation Date',
        dm_elbow: 'Elbow Diameter (cm)',
        dm_knee: 'Knee Diameter (cm)',
        dm_wrist: 'Wrist Diameter (cm)',
        plg_triceps: 'Triceps Fold (mm)',
        plg_bicep: 'Biceps Fold (mm)',
        plg_subscapular: 'Subscapular Fold (mm)',
        plg_suprailiac: 'Suprailiac Fold (mm)',
        plg_supraspinal: 'Supraspinale Fold (mm)',
        plg_abdominal: 'Abdominal Fold (mm)',
        plg_thigh: 'Thigh Fold (mm)',
        plg_calf: 'Calf Fold (mm)',
        plg_armpit: 'Armpit Fold (mm)',
        plg_chest: 'Chest Fold (mm)',
        prm_arm: 'Relaxed Arm Perimeter (cm)',
        prm_arm_contracted: 'Flexed Arm Perimeter (cm)',
        prm_wrist: 'Wrist Perimeter (cm)',
        prm_waist: 'Waist Perimeter (cm)',
        prm_hip: 'Hip Perimeter (cm)',
        prm_calf: 'Calf Perimeter (cm)',
        prm_chest: 'Chest Perimeter (cm)',
        prm_thigh: 'Thigh Perimeter (cm)',
        x: 'x',
        y: 'y',
        fitness_level: 'Fitness Level',
        height: 'Height (cm)',
        weight: 'Weight (kg)',
        creatinine: 'Creatinine',
        t3_t4: 'T3_T4',
        triglycerides: 'Triglycerides',
        uric_acid: 'Uric Acid',
        user_id: 'User',
        referenced_somatotype_id: 'Sport',
        control: 'Control',
        notes: 'Notes',
        resultX: 'Result X',
        resultY: 'Result Y',
        endomorph: 'Endomorph',
        mesomorph: 'Mesomorph',
        ectomorph: 'Ectomorph',
        imc: 'BMI',
        iaks: 'IAKS',
        conicIndex: 'Conic Index',
        sumOfPlgs: 'Sum of Skinfolds',
        fatWeight: 'Fat Weight',
        freeFatWeight: 'Fat-Free Weight',
        desiredIMC: 'Desired BMI',
        complexion: 'Body Frame',
        desiredWeight: 'Desired Weight',
        yhaszFatPercentage: 'Yuhasz Fat %',
        sumaPlieguesEndo: 'Sum of Endo Skinfolds',
        desiredFat2MethodPercentage: 'Desired Fat % (Method 2)',
        yhaszFatPercentageSumaPliegues: 'Yuhasz Fat % (Skinfolds Sum)',
        fatPercentageForPerformance: 'Performance Fat %',
        trainingPeriod: 'Training Period',
        modality: 'Modality',
        birthdayDate: 'Date of Birth',
        gender: 'Gender',
        age: 'Age',
        parizcovaFatPercentage: 'Parizkova Fat %',
        faulknerFatPercentage: 'Faulkner Fat %',
        training_period: 'Training Period',
      },
    },
    food_category: {
      name: 'Category |||| Categories',
      fields: {
        name: 'Name',
        created_at: 'Created',
        updated_at: 'Updated',
      },
    },
    goal: {
      name: 'Goal |||| Goals',
      fields: {
        name: 'Name',
        created_at: 'Created',
        updated_at: 'Updated',
      },
    },
    plan: {
      name: 'Plan |||| Plans',
      summary: 'Plan Summary',
      add_to_plan: 'Add Food to Plan',
      fields: {
        comments: 'Comments',
        goal_id: 'Goal',
        user_id: 'User',
        created_at: 'Created',
        updated_at: 'Updated',
        prescribed_food: 'Foods in Plan',
      },
    },
    food_has_eating_moment: {
      name: 'Food per Moment',
      fields: {
        eating_moment_id: 'Moment',
        food_id: 'Food',
        created_at: 'Created',
        updated_at: 'Updated',
      },
    },
    food: {
      name: 'Food |||| Foods',
      fields: {
        calories: 'Calories',
        chos: 'Carbohydrates',
        description: 'Description',
        fat: 'Fat',
        measure_unit: 'Unit of Measurement',
        protein: 'Protein',
        quantity: 'Quantity',
        created_at: 'Created',
        updated_at: 'Updated',
      },
    },
    food_has_food_category: {
      name: 'Food Category |||| Food Categories',
      fields: {
        food_id: 'Food',
        food_category_id: 'Category',
        created_at: 'Created',
        updated_at: 'Updated',
      },
    },
    eating_moment: {
      name: 'Moment |||| Moments',
      fields: {
        name: 'Name',
        time: 'Time',
        created_at: 'Created',
        updated_at: 'Updated',
      },
    },
    nutritionist: {
      name: 'Nutritionist |||| Nutritionists',
      fields: {
        address: 'Address',
        email: 'Email',
        firstname: 'First Name',
        phone: 'Phone',
        lastname: 'Last Name',
        created_at: 'Created',
        updated_at: 'Updated',
      },
    },
    prescribed_food: {
      name: 'Food in Plan |||| Foods in Plan',
      fields: {
        eating_moment_name: 'Moment',
        eating_moment_time: 'Time',
        food_id: 'Food',
        plan_id: 'Plan',
        prescribed_quantity: 'Prescribed Quantity',
        created_at: 'Created',
        updated_at: 'Updated',
      },
    },
    referenced_somatotype: {
      name: 'Reference Somatotype |||| Reference Somatotypes',
      fields: {
        sport: 'Sport',
        gender: 'Gender',
        endomorph: 'Endomorph',
        mesomorph: 'Mesomorph',
        ectomorph: 'Ectomorph',
        ectomortph: 'Ectomorph',
        x: 'x',
        y: 'y',
        created_at: 'Created',
        updated_at: 'Updated',
      },
    },
    result: {
      name: 'Result |||| Results',
      body_type: 'Body Type',
      tabbed_data: 'Tabulated Data',
      fat_percentages: 'Fat Percentages',
      imc_analysis: 'BMI Analysis',
      x_y_compairson: 'X / Y Comparison',
      x_y_actual: 'Actual Result',
      x_y_referenced: 'Reference',
      historic: 'Results History',
      fields: {
        endomorph: 'Endomorph',
        mesomorph: 'Mesomorph',
        ectomorph: 'Ectomorph',
        resultX: 'Result X',
        resultY: 'Result Y',
        imc: 'BMI',
        iaks: 'IAKS',
        complexion: 'Body Frame',
        raizPT: 'Root PT',
        conicIndex: 'Conic Index',
        sumOfPlgs: 'Sum of Skinfolds',
        sumaPlieguesEndo: 'Sum of Endo Skinfolds',
        endoFactor: 'Endo Factor',
        yhaszFatPercentage: 'Yuhasz Fat %',
        ponderalIndex: 'Ponderal Index',
        faulknerFatPercentage: 'Faulkner Fat %',
        parizcovaFatPercentage: 'Parizkova Fat %',
        fatWeight: 'Fat Weight',
        freeFatWeight: 'Fat-Free Weight',
        activeMass: 'Active Mass',
        residualWeight: 'Residual Weight',
        desiredWeight: 'Desired Weight',
        desiredIMC: 'Desired BMI',
        desiredFat2MethodPercentage: 'Desired Fat % (Method 2)',
      },
    },
    athlete: {
      name: 'Athlete |||| Athletes',
      fields: {
        name: 'Name',
        document: 'Document',
        birth_date: 'Birth Date',
        email: 'Email',
        phone: 'Phone',
        club: 'Club',
        position: 'Position',
      },
    },
    skill_test: {
      name: 'Skill Test |||| Skill Tests',
      fields: {
        cod_sin: 'Cod Without',
        cod_con_el: 'Cod With',
        diff_percentage: 'Difference Percentage',
        d_der: 'Right',
        d_izq: 'Left',
        tl: 'TL',
        athlete: 'Athlete',
        position: 'Position',
      },
    },
    physical_test: {
      name: 'Physical Test |||| Physical Tests',
      fields: {
        position: 'Position',
        weight: 'Weight',
        height: 'Height',
        wingspan: 'Wingspan',
        five_mt: '5 Meters',
        ten_mt: '10 Meters',
        t_test_left: 'T Test Left',
        t_test_right: 'T Test Right',
        cmj: 'CMJ',
        legger: 'Léger',
        athlete: 'Athlete',
      },
    },
  },
  user_list: {
    title: 'Historical Patient Registry',
    subtitle: 'Official Biometric Dossier & Athlete Catalog',
    total_badge: 'Archived Dossiers: %{count}',
    specimen_id: 'Record ID',
    athlete: 'Athlete / Full Name',
    sex: 'Biological Sex',
    contact: 'Contact / Phone',
    birth_age: 'Birthdate & Age',
    specialist: 'Assigned Specialist',
    registered_on: 'Registration Date',
    actions: 'Actions',
    inspect: 'Inspect Dossier',
    empty_title: 'No dossiers recorded',
    empty_desc: 'No subjects were found in this archival ledger.',
    ledger_folio: 'Official Anthropometric Notebook Record • Active Archive Folio',
    active_entries: 'Total Registered: %{count}',
  },
  measurement_list: {
    title: 'Anthropometric Evaluations Ledger',
    subtitle: 'Official Register of ISAK Protocols & Somatocharts',
    total_evaluations: 'Total Evaluations: %{count}',
    control_number: 'Control #',
    athlete: 'Evaluated Athlete',
    evaluation_date: 'Evaluation Date',
    measurements: 'Height / Weight',
    period: 'Period',
    notes: 'Clinical Observations',
    view_sheet: 'View ISAK Sheet',
    view_analytics: 'Somatochart & Analytics',
    empty_title: 'No anthropometric evaluations',
    empty_desc: 'No ISAK evaluation protocols have been logged in the system.',
    ledger_folio: 'Official ISAK Protocol • Longitudinal Evaluation Register',
    active_entries: 'Total Evaluations: %{count}',
  },
  nutritionist_list: {
    title: 'Laboratory Faculty Directory',
    subtitle: 'College of Anthropometrists & Nutritionists of the Laboratory',
    specialists_count: 'Active Specialists: %{count}',
    specialist: 'Specialist',
    credential: 'ISAK Level / Certification',
    contact: 'Direct Contact',
    address: 'Office / Clinic Location',
    registered_on: 'Registration Date',
    actions: 'Actions',
    inspect: 'Professional Profile',
    empty_title: 'No specialists registered',
    empty_desc: 'No nutrition professionals found in the directory.',
    ledger_folio: 'Professional Staff Directory • Official Clinical Board',
    active_entries: 'Active Specialists: %{count}',
  },
  referenced_somatotype_list: {
    title: 'Reference Somatotypes Catalog',
    subtitle: 'Canonical Morphometric Profiles by Discipline & Gender',
    catalog_count: 'Cataloged Profiles: %{count}',
    sport: 'Sport Discipline',
    category: 'Category / Gender',
    endomorphy: 'Endomorphy',
    mesomorphy: 'Mesomorfy',
    ectomorphy: 'Ectomorphy',
    coordinates: 'Coordinates (X, Y)',
    last_update: 'Last Revised',
    inspect: 'View Morphogram',
    empty_title: 'No reference profiles',
    empty_desc: 'No canonical somatotypes have been recorded in the catalog.',
    ledger_folio: 'International Canonical Catalog • Elite Somatotypes',
    active_entries: 'Canonical Profiles: %{count}',
  },
  measurement_form: {
    create_title: 'New ISAK Evaluation Protocol',
    edit_title: 'Edit Anthropometric Evaluation',
    subtitle: 'Official Anthropometric Ledger Workbook & Biometrics',
    section_a: 'Section A: Administrative & Baseline Ledger',
    section_b: 'Section B: Caliper Skinfolds (mm)',
    section_c: 'Section C: Body Perimeters (cm)',
    section_d: 'Section D: Bone Breadths (cm)',
    section_e: 'Section E: Biochemical Markers & Coordinates',
    athlete_select: 'Select Registered Athlete',
    somatotype_select: 'Reference Somatotype',
    specialist_select: 'Responsible Nutritionist',
    control: 'Control Number',
    training_period: 'Training Period',
    evaluation_date: 'Evaluation Date',
    notes: 'Clinical Notes & Observations',
    weight: 'Body Weight (kg)',
    height: 'Stature / Height (cm)',
    wingspan: 'Wingspan (cm)',
    creatinine: 'Serum Creatinine (mg/dL)',
    t3_t4: 'T3 / T4 Thyroid',
    x_coord: 'Somatochart Coordinate X',
    y_coord: 'Somatochart Coordinate Y',
    save: 'Log ISAK Protocol',
    save_changes: 'Save Modifications',
  },
  tour: {
    button_title: 'Tutorial',
    restart_tooltip: 'Launch Atelier Guided Tutorial',
    step_counter: 'Step %{current} of %{total}',
    skip: 'Skip Tutorial',
    previous: 'Previous',
    next: 'Next',
    finish: 'Get Started',
    dont_show_again: "Don't show automatically again",
    step1_title: 'Welcome to the Anthropometry Atelier',
    step1_subtitle: 'Professional Biometrics & Somatotyping Suite',
    step1_desc: 'This digital laboratory allows you to manage confidential athlete dossiers, log standardized ISAK evaluation protocols, and calculate Heath-Carter somatocharts and body composition analytics in real time.',
    step2_title: 'Patient & Athlete Ledgers',
    step2_subtitle: 'Confidentiality & Clinical Isolation',
    step2_desc: 'Each specialist maintains an exclusive catalog of athletes. You can search, sort by ledger columns, and inspect individual dossiers containing demographics, contact records, and clinical history.',
    step3_title: 'ISAK Measurement Protocols',
    step3_subtitle: 'Rigorous Anthropometric Logging',
    step3_desc: 'Record categorized measures: skinfolds (triceps, subscapular, biceps, etc.), body perimeters, and bone breadths, all tied automatically to longitudinal clinical controls.',
    step4_title: 'Analytics, Somatochart & Radar',
    step4_subtitle: 'Heath-Carter Calculus & Elite Baselines',
    step4_desc: 'The biometrics engine instantly plots (X, Y) coordinates and classifies the somatotype (Endomorphic, Mesomorphic, Ectomorphic), with graphical radar overlays against canonical elite sports profiles.',
    step5_title: 'Dynamic Module Configuration',
    step5_subtitle: 'Customize the Atelier to Your Clinical Workflow',
    step5_desc: 'Enable or disable features to fit your clinical focus (patients-only, analytics, PDF reports, reference somatotypes) anytime using the "Modules" control on the top navigation bar.',
    step6_title: 'Professional Profile & Security',
    step6_subtitle: 'Practice Details & Credential Security',
    step6_desc: 'Update your clinic phone, practice address, and securely update your password with salted encryption by clicking your specialist badge or the user profile menu.',
  },
  profile: {
    title: 'Professional Profile',
    subtitle: 'Practice Details & Credential Security',
    specialist_id: 'Specialist ID',
    role: 'Assigned Role',
    assigned_patients: 'Assigned Dossiers',
    firstname: 'First Name',
    lastname: 'Last Name',
    email: 'Email (Institutional)',
    phone: 'Contact Phone / WhatsApp',
    address: 'Clinic / Office Address',
    change_password_title: 'Secure Password Update',
    current_password: 'Current Password',
    new_password: 'New Password',
    confirm_password: 'Confirm New Password',
    password_strength: 'Password Strength',
    password_mismatch: 'New passwords do not match.',
    current_password_required: 'You must enter your current password to confirm changes.',
    password_weak: 'New password must be at least 8 characters and combine letters and numbers.',
    save: 'Save Changes',
    saving: 'Saving...',
    save_success: 'Profile and credentials updated successfully.',
    save_error: 'Error updating profile. Please check your current password.',
    close: 'Close',
  },
  forgot_password: {
    link: 'Forgot your password?',
    title: 'Professional Access Assistance',
    subtitle: 'Security Protocol & Credential Recovery',
    description: 'Enter the institutional email address registered with your clinical practice. Your account will be verified and an authorized recovery protocol will be coordinated.',
    email: 'Registered Email Address',
    send: 'Request Assistance',
    cancel: 'Return to Login',
    status_sent: 'Request logged. In accordance with medical confidentiality standards, a security notification has been sent to coordinate secure access recovery.',
    contact_admin: 'For urgent clinical access, contact Technical Administration directly.',
  },
  import_csv: {
    button: 'Import Patients (CSV)',
    title: 'Bulk Athlete & Patient Import',
    subtitle: 'Automatic Assignment to Your Clinical Practice',
    instructions: 'Download the official CSV template, fill in your athletes demographic data, and upload the file. All imported patients will be automatically linked to your roster.',
    download_template: 'Download CSV Template',
    select_file: 'Select CSV File (.csv)',
    drop_file: 'Click to select or drag and drop a CSV file here',
    preview_title: 'Dossiers to Import (%{count} valid records)',
    import_now: 'Confirm & Import to Atelier',
    importing: 'Importing records to database...',
    success: 'Import complete! Successfully registered %{count} patients.',
    error: 'Import error: %{message}',
    no_file: 'Please select a valid CSV file before continuing.',
    col_firstname: 'First Name',
    col_lastname: 'Last Name',
    col_email: 'Email',
    col_phone: 'Phone',
    col_gender: 'Gender',
    col_birthday: 'Birthday',
    col_address: 'Address',
    cancel: 'Cancel',
  },
};

